import { useConfigQuery } from '~/api/hooks'

/** Every step of the registration flow, in order (face-first spec §2.1). `document` is the optional one. */
export const STEP_KEYS = ['about', 'email', 'place', 'face', 'document'] as const
export type StepKey = (typeof STEP_KEYS)[number]

/**
 * The steps of the flow: 4 while the identity-document requirement is off, 5 (the document after the face check) when
 * on. No password step (passwordless spec §2): the registration ends at the face check or the document.
 */
export function stepsFor(required: boolean): readonly StepKey[] {
  return required ? STEP_KEYS : STEP_KEYS.filter((k) => k !== 'document')
}

/** The 1-based number of `key` in the flow. A step that is not in the flow (document while off) is a programming error. */
export function stepNumber(key: StepKey, required: boolean): number {
  const i = stepsFor(required).indexOf(key)
  if (i < 0) throw new Error(`step ${key} is not in the flow`)
  return i + 1
}

/**
 * The flow for the public policy (`GET /api/public/config` `document.required`): 4 steps until the server says the
 * document is required (5), so the loading state and an unreachable config both show the shorter flow. The count is
 * the web's own (`stepsFor`), never `document.steps` of the config, which still counts the removed password step.
 */
export function useFlowSteps() {
  const required = useConfigQuery().data?.document.required ?? false
  const steps = stepsFor(required)
  return { required, steps, total: steps.length, numberOf: (key: StepKey) => stepNumber(key, required) }
}

/**
 * The public config for a page that must not guess: `pending` only before the first answer, `failed` once it has
 * failed (also while a retry is running: TanStack puts a data-less failed query back to `pending` whenever a new
 * observer mounts, which would unmount the page that shows the error and loop).
 */
export function useConfigGate() {
  const config = useConfigQuery()
  const failed = config.isError || (config.isPending && config.errorUpdatedAt > 0)
  return { config, failed, pending: config.isPending && !failed }
}
